import { z } from 'zod';

const nonNegMoney = z.string().regex(/^\d+(\.\d{1,4})?$/, 'must be a non-negative money value');
const monthNum = z.number().int().min(1).max(12);
const year = z.number().int().min(2000).max(2100);

export const budgetEntryInputSchema = z.object({
  accountId: z.string().uuid(),
  month: monthNum,
  amount: nonNegMoney,
});
export type BudgetEntryInput = z.infer<typeof budgetEntryInputSchema>;

export const setBudgetsInputSchema = z.object({
  year,
  entries: z.array(budgetEntryInputSchema),
});
export type SetBudgetsInput = z.infer<typeof setBudgetsInputSchema>;

export const importBudgetInputSchema = z.object({
  year,
  fileName: z.string().trim().min(1).max(255),
  /** base64-encoded .xlsx (no data: prefix). */
  content: z.string().min(1),
});
export type ImportBudgetInput = z.infer<typeof importBudgetInputSchema>;

export const budgetsQuerySchema = z.object({
  year: z.coerce.number().int().min(2000).max(2100),
});
export type BudgetsQuery = z.infer<typeof budgetsQuerySchema>;

export const budgetVsActualQuerySchema = z.object({
  year: z.coerce.number().int().min(2000).max(2100),
  fromMonth: z.coerce.number().int().min(1).max(12).optional(),
  toMonth: z.coerce.number().int().min(1).max(12).optional(),
});
export type BudgetVsActualQuery = z.infer<typeof budgetVsActualQuerySchema>;

// ── Editable grid ───────────────────────────────────────────────────────────

export interface BudgetGridRow {
  accountId: string;
  code: string;
  name: string;
  accountType: string;
  /** 12 monthly amounts (Jan..Dec), 2-decimal strings. */
  months: string[];
  total: string;
}

export interface BudgetGrid {
  year: number;
  rows: BudgetGridRow[];
}

// ── Import ──────────────────────────────────────────────────────────────────

export interface BudgetImportResult {
  year: number;
  imported: number; // cells written
  unknownCodes: string[];
  errors: { row: number; message: string }[];
}

// ── Budget vs Actual report ─────────────────────────────────────────────────

export interface BudgetVsActualRow {
  accountId: string;
  code: string;
  name: string;
  accountType: string;
  budget: string;
  actual: string;
  /** Signed so positive = favorable (revenue over budget / expense under budget). */
  variance: string;
  favorable: boolean;
}

export interface BudgetVsActualResponse {
  year: number;
  fromMonth: number;
  toMonth: number;
  rows: BudgetVsActualRow[];
  totals: {
    budgetIncome: string;
    actualIncome: string;
    budgetExpense: string;
    actualExpense: string;
    netBudget: string;
    netActual: string;
    netVariance: string;
    netFavorable: boolean;
  };
}
